import type { GSplatVisualShowcaseEffectOptions } from './GSplatVisualShowcaseEffect'
import { GSplatVisualShowcaseEffect } from './GSplatVisualShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const wavesShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uIntensity;
uniform vec3 uCenter;
uniform float uEffectScale;

${SHOWCASE_SHADER_COMMON_GLSL}

void modifySplatCenter(inout vec3 center) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float radius = length(local.xy);
  float wave = sin(radius * 7.5 - t * 4.0);
  float ripple = wave * 0.12 * uIntensity;

  center.z += ripple * uEffectScale;
  center.xy += normalize(local.xy + vec2(0.0001)) * cos(radius * 6.0 - t * 3.0) * 0.04 * uIntensity * uEffectScale;
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  float t = uTime * uTimeScale;
  float radius = length((originalCenter - uCenter).xy / uEffectScale);
  float crest = sin(radius * 7.5 - t * 4.0) * 0.5 + 0.5;
  gsplatApplyUniformScale(covA, covB, 1.0 + crest * 0.26 * uIntensity);
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float crest = sin(length(local.xy) * 7.5 - t * 4.0) * 0.5 + 0.5;
  vec3 waveColor = mix(vec3(0.05, 0.28, 0.82), vec3(0.28, 0.94, 0.88), crest);
  color.rgb = mix(color.rgb, waveColor, 0.42 * uIntensity);
}
`

const wavesShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = (*center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length(local.xy);
  let wave = sin(radius * 7.5 - t * 4.0);
  let ripple = wave * 0.12 * uniform.uIntensity;
  let lateral = normalize(local.xy + vec2f(0.0001)) *
    cos(radius * 6.0 - t * 3.0) * 0.04 *
    uniform.uIntensity * uniform.uEffectScale;
  (*center) = vec3f(
    (*center).x + lateral.x,
    (*center).y + lateral.y,
    (*center).z + ripple * uniform.uEffectScale
  );
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let t = uniform.uTime * uniform.uTimeScale;
  let radius = length((originalCenter - uniform.uCenter).xy / uniform.uEffectScale);
  let crest = sin(radius * 7.5 - t * 4.0) * 0.5 + 0.5;
  *scale = (*scale) * (1.0 + crest * 0.26 * uniform.uIntensity);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = (center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let crest = sin(length(local.xy) * 7.5 - t * 4.0) * 0.5 + 0.5;
  let waveColor = mix(vec3f(0.05, 0.28, 0.82), vec3f(0.28, 0.94, 0.88), crest);
  (*color) = vec4f(mix((*color).rgb, waveColor, 0.42 * uniform.uIntensity), (*color).a);
}
`

export class GSplatVisualWavesEffect extends GSplatVisualShowcaseEffect {
  constructor(options: GSplatVisualShowcaseEffectOptions = {}) {
    super(
      {
        id: 'visual-waves',
        shader: wavesShaderGLSL,
        shaderWGSL: wavesShaderWGSL,
      },
      options
    )
  }
}
